import { domToBlob } from 'modern-screenshot';

// Rendered at twice the CSS size so the PNG stays crisp on high-DPI phone screens.
const CAPTURE_SCALE = 2;

/**
 * Renders a DOM node to a PNG and hands it to the user.
 *
 * On mobile browsers that support sharing files this opens the native share sheet
 * ("Save to Photos", WhatsApp, Discord, …). Everywhere else it falls back to a plain
 * download, using the same anchor-click idiom as APIRoutes.exportUsers.
 *
 * Resolves silently when the user dismisses the share sheet — that is not an error.
 */
export async function sharePngOrDownload(node: HTMLElement, fileName: string, title: string): Promise<void> {
  const blob = await domToBlob(node, {
    scale: CAPTURE_SCALE,
    type: 'image/png',
    // The route only uses the system font stack, so there is nothing to embed and
    // skipping the font pass avoids fetching and inlining every stylesheet.
    font: false
  });

  const file = new File([blob], fileName, { type: 'image/png' });

  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title });
      return;
    } catch (err) {
      // The user closed the share sheet - nothing went wrong, so stay quiet.
      if (err instanceof DOMException && err.name === 'AbortError') return;
      throw err;
    }
  }

  downloadBlob(blob, fileName);
}

function downloadBlob(blob: Blob, fileName: string): void {
  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(downloadUrl);
}

/**
 * Builds a file-system safe PNG name, e.g. "bolzai-swing-NASDAQ-CRML.png".
 * Tickers can contain characters like the ':' in "NASDAQ:CRML".
 */
export function buildImageFileName(...parts: string[]): string {
  const slug = parts
    .join('-')
    .replace(/[^a-zA-Z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');

  return `${slug}.png`;
}
